/**
 * section-steps.js
 * Impact Theme – Responsive sine-wave connector for the Step Section.
 *
 * - Measures each .steps-section__circle's real bounding box after paint
 * - Draws ONE continuous cubic-Bézier sine wave path connecting all circles
 * - Re-draws on window resize (debounced)
 * - Re-draws when section reloads in the Shopify Theme Editor
 * - Supports multiple Step Section instances on the same page
 */

(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 989; // must match CSS
  var WAVE_AMPLITUDE    = 100;  // px — how much the arc bows up/down

  /**
   * Draw (or redraw) the sine wave for one section instance.
   * @param {string} sectionId – Shopify section.id
   */
  function drawWave(sectionId) {
    var wrapper  = document.getElementById('stepsWrapper--' + sectionId);
    var svg      = document.getElementById('sineSvg--'     + sectionId);
    var path     = document.getElementById('sinePath--'    + sectionId);

    if (!wrapper || !svg || !path) return;

    // On mobile the SVG is hidden — clear path and skip
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      path.setAttribute('d', '');
      return;
    }

    var circles = Array.prototype.slice.call(
      wrapper.querySelectorAll('.steps-section__circle')
    );
    if (circles.length < 2) return;

    var wrapperRect = wrapper.getBoundingClientRect();
    var W = wrapper.offsetWidth;
    var H = wrapper.offsetHeight;

    // Sync SVG canvas size to wrapper
    svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    svg.style.width  = W + 'px';
    svg.style.height = H + 'px';

    // Collect each circle's centre + radius relative to the wrapper
    var centers = circles.map(function (circle) {
      var rect = circle.getBoundingClientRect();
      return {
        x: rect.left - wrapperRect.left + rect.width  / 2,
        y: rect.top  - wrapperRect.top  + rect.height / 2,
        r: rect.width / 2
      };
    });

    var n = centers.length;

    // ── Anchor points ──────────────────────────────────────────────────────────
    // • First circle  → attach at its RIGHT edge
    // • Middle circles → pass through their CENTRE  (wave flows through them)
    // • Last circle   → attach at its LEFT edge
    // This makes the path one unbroken line rather than two disconnected arcs.
    var anchors = centers.map(function (c, i) {
      if (i === 0)     return { x: c.x + c.r, y: c.y };
      if (i === n - 1) return { x: c.x - c.r, y: c.y };
      return { x: c.x, y: c.y };
    });

    // ── Build a single continuous cubic-Bézier path ────────────────────────────
    // One M at the start, then one C per segment — no extra M commands.
    // Control points sit at the horizontal midpoint of each segment, offset
    // vertically by ±WAVE_AMPLITUDE, alternating so the wave is smooth.
    var d = 'M ' + anchors[0].x.toFixed(1) + ' ' + anchors[0].y.toFixed(1);

    for (var i = 0; i < anchors.length - 1; i++) {
      var a  = anchors[i];
      var b  = anchors[i + 1];
      var mx = ((a.x + b.x) / 2).toFixed(1);

      // Even segments bow UP, odd segments bow DOWN → clean sine shape
      var bend = (i % 2 === 0) ? -WAVE_AMPLITUDE : WAVE_AMPLITUDE;

      var cp1y = (a.y + bend).toFixed(1);
      var cp2y = (b.y + bend).toFixed(1);

      d += ' C ' + mx   + ' ' + cp1y +
           ', '  + mx   + ' ' + cp2y +
           ', '  + b.x.toFixed(1) + ' ' + b.y.toFixed(1);
    }

    path.setAttribute('d', d);
  }

  /**
   * Initialise one section: draw immediately, wire up resize + image listeners.
   * @param {string} sectionId
   */
  function initSection(sectionId) {
    var wrapper = document.getElementById('stepsWrapper--' + sectionId);
    if (!wrapper) return;
    if (wrapper.dataset.sineInit === '1') return;
    wrapper.dataset.sineInit = '1';

    // Double rAF ensures Impact's own layout JS has already run
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        drawWave(sectionId);
      });
    });

    // Redraw on resize (debounced)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        drawWave(sectionId);
      }, 80);
    });

    // Redraw if icon images load late and shift layout
    wrapper.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', function () {
          drawWave(sectionId);
        });
      }
    });
  }

  /**
   * Public entry point called from the Liquid inline <script>.
   */
  window.__stepsSineInit = function (sectionId) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initSection(sectionId);
      });
    } else {
      initSection(sectionId);
    }
  };

  /**
   * Shopify Theme Editor – re-init after a live section reload.
   */
  document.addEventListener('shopify:section:load', function (e) {
    var id = e.detail && e.detail.sectionId;
    if (!id) return;
    var wrapper = document.getElementById('stepsWrapper--' + id);
    if (!wrapper) return;
    delete wrapper.dataset.sineInit;
    initSection(id);
  });

}());